import { useState, useEffect, useCallback } from 'react';

interface FullscreenLandscapeState {
  isFullscreen: boolean;
  isLandscape: boolean;
  /** Whether the device supports the Fullscreen API */
  supportsFullscreen: boolean;
  /** Request fullscreen + landscape lock (must be called from a user gesture) */
  enterFullscreen: () => Promise<void>;
  /** Exit fullscreen */
  exitFullscreen: () => Promise<void>;
}

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type OrientationLockable = ScreenOrientation & {
  lock?: (orientation: 'landscape' | 'portrait' | string) => Promise<void>;
};

export function useFullscreenLandscape(): FullscreenLandscapeState {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > window.innerHeight;
  });

  const supportsFullscreen =
    typeof document !== 'undefined' &&
    !!(
      document.documentElement.requestFullscreen ||
      (document.documentElement as WebkitFullscreenElement).webkitRequestFullscreen
    );

  // Track fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElement =
        document.fullscreenElement ||
        (document as WebkitFullscreenDocument).webkitFullscreenElement;
      setIsFullscreen(!!fsElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
    };
  }, []);

  // Track orientation changes
  useEffect(() => {
    const updateOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', updateOrientation);
    screen.orientation?.addEventListener('change', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
      screen.orientation?.removeEventListener('change', updateOrientation);
    };
  }, []);

  const enterFullscreen = useCallback(async () => {
    const el = document.documentElement as WebkitFullscreenElement;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      }

      // After entering fullscreen, try to lock orientation
      try {
        await (screen.orientation as OrientationLockable | undefined)?.lock?.(
          'landscape'
        );
      } catch {
        // Orientation lock is not supported or not allowed — that's fine
      }
    } catch {
      // Fullscreen request was denied (e.g. not from user gesture)
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      const doc = document as WebkitFullscreenDocument;
      if (doc.exitFullscreen) {
        await doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      }
    } catch {
      // Already not in fullscreen
    }
  }, []);

  return {
    isFullscreen,
    isLandscape,
    supportsFullscreen,
    enterFullscreen,
    exitFullscreen,
  };
}
